import { IPayrollReport } from "../models/Payroll.ts";
import {
  buildReciboDailyPaymentsFromPaymentDetail,
  collectReciboDeductionLines,
  computePartnerPayBreakdownFromDetail,
  formatPayrollMoneyAmount,
  formatPayrollMoneyLabel,
  getReciboJornadasCompletasCuadroDisplay,
  groupDeductionsForRecibo,
  isReciboRestDayEnjoyedRow,
  isReciboRestDayWorkedRow,
  isTarjetasDeductionName,
  normalizeDeductionGroupKey,
  parseDeductionAmount,
  parsePayrollMoneyValue,
  splitPayrollDeductionTotalsForCuadro,
  sumExtraHoursFromDailyPayments,
  summarizeChartDays,
  type GroupedDeductionRow,
} from "./payrollUtils.ts";
import { mergeDailyPaymentsByDate } from "./payrollVisitHours.ts";
import { VACATION_DAILY_PREMIUM_RATE } from "../services/payroll/payPremiums.ts";

export type CuadroExtendedMetric = {
  qty: number | string;
  amount: number;
};

export type CuadroExtendedBonus = {
  name: string;
  amount: number;
};

export type CuadroExtendedRowBreakdown = {
  jornadasCompletas: CuadroExtendedMetric;
  horasTrabajadas: CuadroExtendedMetric;
  horasNormales: CuadroExtendedMetric;
  vacaciones: CuadroExtendedMetric;
  primaVacacional: number;
  incapacidad: CuadroExtendedMetric;
  horasExtras: CuadroExtendedMetric;
  turnosDobles: CuadroExtendedMetric;
  descansoTrabajado: CuadroExtendedMetric;
  descansoDisfrutado: CuadroExtendedMetric;
  diasFestivos: CuadroExtendedMetric;
  bonos: CuadroExtendedBonus[];
  totalIngresos: number;
  deducciones: CuadroExtendedBonus[];
  totalDeducciones: number;
  tarjetas: number;
  totalAPagar: number;
};

export type BuildCuadroExtendedInput = {
  record: IPayrollReport;
  fullDayHours: number;
  paymentDetail?: {
    dailyPayments?: any[];
    calculatedBonuses?: any[];
    calculatedNewDeductions?: any[];
    freeDaysEnjoyedDates?: string[];
    paymentDetails?: any;
  } | null;
  chartFreeDaysDates?: string[];
  savedIncidents?: any[];
};

function parseHoursQty(value: unknown): number {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const raw = String(value ?? "");
  const match = raw.match(/^(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : 0;
}

function emptyMetric(): CuadroExtendedMetric {
  return { qty: "", amount: 0 };
}

function buildFromDailyPayments(
  record: IPayrollReport,
  dailyPayments: any[],
  fullDayHours: number
): Omit<
  CuadroExtendedRowBreakdown,
  "bonos" | "deducciones" | "totalDeducciones" | "tarjetas" | "totalAPagar" | "totalIngresos"
> {
  const dailySalary = parsePayrollMoneyValue(record.dailySalary);
  const hourlyPay = parsePayrollMoneyValue(record.payPerHour);

  let importeJornadas = 0;
  let horasPagosPorHora = 0;
  let importePagosPorHora = 0;
  let turnoDobleCount = 0;
  let importeTurnoDoble = 0;
  let vacacionesCount = 0;
  let importeVacaciones = 0;
  let incapacidadCount = 0;
  let importeIncapacidad = 0;

  dailyPayments.forEach((day: any) => {
    const concept = String(day.concept || "").toLowerCase();
    const hours = parseFloat(String(day.hours || "0"));
    const payment = parsePayrollMoneyValue(day.payment);

    if (isReciboRestDayWorkedRow(day) || isReciboRestDayEnjoyedRow(day)) {
      return;
    }
    if (
      concept.includes("festivo trabajado") ||
      concept.includes("día festivo")
    ) {
      return;
    }
    if (concept.includes("incapacidad")) {
      incapacidadCount++;
      importeIncapacidad += payment;
      return;
    }
    if (concept.includes("vacaciones")) {
      vacacionesCount++;
      importeVacaciones += payment;
      return;
    }
    if (concept.includes("doble jornada") || hours >= 16.5) {
      turnoDobleCount++;
      importeTurnoDoble += payment;
      return;
    }
    if (
      hours > 0 &&
      hours < fullDayHours &&
      (concept.includes("pago por hora") || !concept.includes("jornada completa"))
    ) {
      let conceptHours = hours;
      if (concept.includes("× pago por hora")) {
        const match = concept.match(/(\d+(?:\.\d+)?)\s*h\s*×\s*pago por hora/i);
        if (match) conceptHours = parseFloat(match[1]);
      }
      horasPagosPorHora += conceptHours;
      return;
    }
    if (concept.includes("jornada completa") && concept.includes("extras")) {
      importeJornadas += dailySalary;
      return;
    }
    if (concept.includes("jornada completa")) {
      importeJornadas += dailySalary;
      return;
    }
    if (hours >= fullDayHours) {
      importeJornadas += dailySalary;
    }
  });

  importePagosPorHora = horasPagosPorHora * hourlyPay;

  const horasExtrasCount = sumExtraHoursFromDailyPayments(
    dailyPayments,
    fullDayHours
  );
  const importeHorasExtras = horasExtrasCount * hourlyPay;

  const jornadasCuadro = getReciboJornadasCompletasCuadroDisplay(
    dailyPayments,
    fullDayHours
  );

  const freeDaysWorkedCount = dailyPayments.filter((day) =>
    isReciboRestDayWorkedRow(day)
  ).length;
  const freeDaysWorkedAmount = dailyPayments.reduce((sum, day) => {
    if (!isReciboRestDayWorkedRow(day)) return sum;
    return sum + parsePayrollMoneyValue(day.payment);
  }, 0);

  const freeDaysEnjoyedCount = dailyPayments.filter((day) =>
    isReciboRestDayEnjoyedRow(day)
  ).length;
  const freeDaysEnjoyedAmount = freeDaysEnjoyedCount * dailySalary;

  const celebrityDaysWorked = Number(record.celebrityDaysWorked || 0);
  const celebrityAmount =
    parsePayrollMoneyValue(record.celebrityDaysPayTotal) ||
    celebrityDaysWorked * dailySalary;

  const horasTrabajadasQty = dailyPayments.reduce((sum, day) => {
    if (isReciboRestDayEnjoyedRow(day)) return sum;
    return sum + parseFloat(String(day.hours || "0"));
  }, 0);

  const pd = record.paymentDetails;
  const horasNormalesQty =
    parseHoursQty(pd?.hoursAuthorized) ||
    parseHoursQty(record.authorizedHours) ||
    jornadasCuadro.horasNominales + horasPagosPorHora;
  const horasNormalesAmount =
    importeJornadas + importePagosPorHora ||
    parsePayrollMoneyValue(pd?.hoursAuthorizedPay);

  const primaVacacional =
    vacacionesCount > 0
      ? vacacionesCount * dailySalary * VACATION_DAILY_PREMIUM_RATE
      : 0;

  const jornadasQty = jornadasCuadro.dias || record.jornadasCompletas || 0;

  const horasTrabajadasQtyFinal =
    horasTrabajadasQty > 0
      ? horasTrabajadasQty
      : parseHoursQty(record.totalHoursWorked);

  const freeDaysWorkedQty =
    freeDaysWorkedCount || Number(record.freeDaysWorked || 0);
  const freeDaysWorkedAmountFinal =
    freeDaysWorkedAmount > 0
      ? freeDaysWorkedAmount
      : freeDaysWorkedQty > 0
        ? freeDaysWorkedQty * dailySalary
        : parsePayrollMoneyValue(pd?.freeDayWorkedPayTotal);

  return {
    jornadasCompletas: {
      qty: jornadasQty,
      amount: jornadasQty * dailySalary,
    },
    horasTrabajadas: {
      qty:
        horasTrabajadasQty > 0
          ? horasTrabajadasQty.toFixed(1)
          : parseHoursQty(record.totalHoursWorked),
      amount: horasTrabajadasQtyFinal * hourlyPay,
    },
    horasNormales: {
      qty: horasNormalesQty > 0 ? horasNormalesQty.toFixed(1) : "",
      amount: horasNormalesAmount,
    },
    vacaciones: {
      qty: vacacionesCount,
      amount: importeVacaciones,
    },
    primaVacacional,
    incapacidad: {
      qty: incapacidadCount,
      amount: importeIncapacidad,
    },
    horasExtras: {
      qty: horasExtrasCount > 0 ? horasExtrasCount : 0,
      amount: importeHorasExtras,
    },
    turnosDobles: {
      qty: turnoDobleCount || Number(record.doubleShifts || 0),
      amount:
        importeTurnoDoble > 0
          ? importeTurnoDoble
          : parsePayrollMoneyValue(pd?.doubleShiftPayTotal),
    },
    descansoTrabajado: {
      qty: freeDaysWorkedQty,
      amount: freeDaysWorkedAmountFinal,
    },
    descansoDisfrutado: {
      qty: freeDaysEnjoyedCount || Number(record.freeDaysEnjoyed || 0),
      amount:
        freeDaysEnjoyedAmount > 0
          ? freeDaysEnjoyedAmount
          : parsePayrollMoneyValue(pd?.freeDaysEnjoyedPay),
    },
    diasFestivos: {
      qty: celebrityDaysWorked,
      amount: celebrityAmount,
    },
  };
}

function buildFromSummaryFallback(
  record: IPayrollReport,
  fullDayHours: number
): Omit<
  CuadroExtendedRowBreakdown,
  "bonos" | "deducciones" | "totalDeducciones" | "tarjetas" | "totalAPagar" | "totalIngresos"
> {
  const pd = record.paymentDetails;
  const dailySalary = parsePayrollMoneyValue(record.dailySalary);
  const hourlyPay = parsePayrollMoneyValue(record.payPerHour);
  const jornadas = Number(record.jornadasCompletas || 0);
  const totalHoursQty = parseHoursQty(record.totalHoursWorked);
  const freeDaysWorkedQty = Number(record.freeDaysWorked || 0);

  return {
    jornadasCompletas: {
      qty: jornadas,
      amount: jornadas * dailySalary,
    },
    horasTrabajadas: {
      qty: totalHoursQty,
      amount: totalHoursQty * hourlyPay || parsePayrollMoneyValue(pd?.hoursAuthorizedPay),
    },
    horasNormales: {
      qty: parseHoursQty(record.authorizedHours || pd?.hoursAuthorized),
      amount: parsePayrollMoneyValue(pd?.hoursAuthorizedPay),
    },
    vacaciones: emptyMetric(),
    primaVacacional: 0,
    incapacidad: emptyMetric(),
    horasExtras: {
      qty: parseHoursQty(record.extraHours),
      amount: parsePayrollMoneyValue(pd?.extraHourPayTotal),
    },
    turnosDobles: {
      qty: Number(record.doubleShifts || 0),
      amount: parsePayrollMoneyValue(pd?.doubleShiftPayTotal),
    },
    descansoTrabajado: {
      qty: freeDaysWorkedQty,
      amount:
        parsePayrollMoneyValue(pd?.freeDayWorkedPayTotal) ||
        (freeDaysWorkedQty > 0 ? freeDaysWorkedQty * dailySalary : 0),
    },
    descansoDisfrutado: {
      qty: Number(record.freeDaysEnjoyed || 0),
      amount: parsePayrollMoneyValue(pd?.freeDaysEnjoyedPay),
    },
    diasFestivos: {
      qty: Number(record.celebrityDaysWorked || 0),
      amount: parsePayrollMoneyValue(record.celebrityDaysPayTotal),
    },
  };
}

export function buildCuadroExtendedRowBreakdown(
  input: BuildCuadroExtendedInput
): CuadroExtendedRowBreakdown {
  const { record, fullDayHours, paymentDetail, chartFreeDaysDates, savedIncidents } =
    input;

  let perceptions: Omit<
    CuadroExtendedRowBreakdown,
    "bonos" | "deducciones" | "totalDeducciones" | "tarjetas" | "totalAPagar" | "totalIngresos"
  >;

  const rawDaily = paymentDetail?.dailyPayments?.length
    ? mergeDailyPaymentsByDate(paymentDetail.dailyPayments)
    : [];

  if (rawDaily.length > 0) {
    const reciboDays = buildReciboDailyPaymentsFromPaymentDetail({
      backendDailyPayments: rawDaily,
      dailySalary: parsePayrollMoneyValue(record.dailySalary),
      hourlyPay: parsePayrollMoneyValue(record.payPerHour),
      NormalCalculationNomina: record.NormalCalculationNomina || false,
      fullDayHours,
      chartFreeDaysDates: chartFreeDaysDates || [],
      freeDaysEnjoyedDates: paymentDetail?.freeDaysEnjoyedDates || [],
      savedIncidents: savedIncidents || [],
    });
    perceptions = buildFromDailyPayments(record, reciboDays, fullDayHours);
  } else {
    perceptions = buildFromSummaryFallback(record, fullDayHours);
  }

  const bonusesSource =
    record.calculatedBonuses?.length
      ? record.calculatedBonuses
      : paymentDetail?.calculatedBonuses?.length
        ? paymentDetail.calculatedBonuses
        : [];

  const bonos: CuadroExtendedBonus[] = bonusesSource
    .map((bonus: any) => ({
      name: String(bonus?.name || bonus?.incentive?.name || "Bono").trim(),
      amount: parseDeductionAmount(bonus?.calculatedAmount ?? bonus?.amount),
    }))
    .filter((b) => b.amount > 0);

  const totalBonosFromField = parsePayrollMoneyValue(record.totalIncentives);
  if (bonos.length === 0 && totalBonosFromField > 0) {
    bonos.push({ name: "Bonos", amount: totalBonosFromField });
  }

  const rawDeductionLines = collectReciboDeductionLines({
    deductionBreakdown:
      (record.paymentDetails as { deductionBreakdown?: unknown[] } | undefined)
        ?.deductionBreakdown ??
      paymentDetail?.paymentDetails?.deductionBreakdown,
    calculatedNewDeductions: record.calculatedNewDeductions?.length
      ? record.calculatedNewDeductions
      : paymentDetail?.calculatedNewDeductions,
    calculatedMissingBoxes: record.calculatedMissingBoxes,
  });

  const uniformAmount = parsePayrollMoneyValue(record.uniformDeduction);
  if (uniformAmount > 0) {
    rawDeductionLines.push({ name: "Uniforme", amount: uniformAmount });
  }

  const allDeducciones: CuadroExtendedBonus[] = rawDeductionLines.map((line) => ({
    name: line.name,
    amount: line.amount,
  }));

  const split = splitPayrollDeductionTotalsForCuadro(record);
  if (split.tarjetas > 0) {
    const hasTarjetas = allDeducciones.some((ded) =>
      isTarjetasDeductionName(ded.name)
    );
    if (!hasTarjetas) {
      allDeducciones.push({ name: "Tarjetas", amount: split.tarjetas });
    }
  }

  const perceptionTotal =
    perceptions.jornadasCompletas.amount +
    perceptions.horasExtras.amount +
    perceptions.turnosDobles.amount +
    perceptions.descansoTrabajado.amount +
    perceptions.descansoDisfrutado.amount +
    perceptions.diasFestivos.amount +
    perceptions.vacaciones.amount +
    perceptions.primaVacacional +
    perceptions.incapacidad.amount +
    bonos.reduce((s, b) => s + b.amount, 0);

  const breakdownTotal = computePartnerPayBreakdownFromDetail({
    paymentDetail: paymentDetail || { dailyPayments: rawDaily },
    chartFreeDaysDates,
    savedIncidents,
    record,
    fullDayHours,
  });

  const totalAPagar =
    breakdownTotal?.totalPayWithIncentives ??
    parsePayrollMoneyValue(record.totalPayWithIncentives);

  return {
    ...perceptions,
    bonos,
    totalIngresos: perceptionTotal,
    deducciones: allDeducciones,
    totalDeducciones: split.totalAll,
    tarjetas: split.tarjetas,
    totalAPagar,
  };
}

export function parseCuadroExtendedQty(qty: number | string): number {
  return parseHoursQty(qty);
}

export function formatExtendedQty(qty: number | string): string {
  if (qty === "" || qty === null || qty === undefined) return "-";
  if (typeof qty === "number") return qty > 0 ? String(qty) : "-";
  const str = String(qty).trim();
  if (!str || str === "0") return "-";
  return str;
}

export function formatExtendedAmount(amount: number): string {
  return amount > 0 ? formatPayrollMoneyAmount(amount) : "$ -";
}

export function isExtendedEmptyDisplay(text: string): boolean {
  const normalized = String(text ?? "").trim();
  return normalized === "-" || normalized === "$ -";
}

export const EXTENDED_EMPTY_TEXT_STYLE = { opacity: 0.32 } as const;

export const CUADRO_EXTENDED_COL_INFO = 1;
export const CUADRO_EXTENDED_COL_SUELDOS = 3;
/** 7 pares cantidad/$ + prima vac. + festivo cant/$ */
export const CUADRO_EXTENDED_COL_PERCEPCIONES = 17;
export const CUADRO_EXTENDED_COL_TOTAL_INGRESOS = 1;
export const CUADRO_EXTENDED_COL_TOTAL_DEDUCCIONES = 1;
export const CUADRO_EXTENDED_COL_TOTAL_PAGAR = 1;
export const CUADRO_EXTENDED_COL_FORMA_PAGO = 2;
export const CUADRO_EXTENDED_COL_ACCIONES = 1;

export type CuadroExtendedColumnSchema = {
  bonusColumns: string[];
  deductionColumns: string[];
  columnCount: number;
};

export function normalizeBonusColumnKey(name: string): string {
  return (name || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export function getCuadroExtendedColumnCount(
  bonusColumnCount: number,
  deductionColumnCount: number
): number {
  return (
    CUADRO_EXTENDED_COL_INFO +
    CUADRO_EXTENDED_COL_SUELDOS +
    CUADRO_EXTENDED_COL_PERCEPCIONES +
    bonusColumnCount +
    CUADRO_EXTENDED_COL_TOTAL_INGRESOS +
    deductionColumnCount +
    CUADRO_EXTENDED_COL_TOTAL_DEDUCCIONES +
    CUADRO_EXTENDED_COL_TOTAL_PAGAR +
    CUADRO_EXTENDED_COL_FORMA_PAGO +
    CUADRO_EXTENDED_COL_ACCIONES
  );
}

export function getCuadroExtendedPercepcionesGroupColSpan(
  bonusColumnCount: number
): number {
  return (
    CUADRO_EXTENDED_COL_INFO +
    CUADRO_EXTENDED_COL_SUELDOS +
    CUADRO_EXTENDED_COL_PERCEPCIONES +
    bonusColumnCount +
    CUADRO_EXTENDED_COL_TOTAL_INGRESOS
  );
}

export function getCuadroExtendedDeduccionesColSpan(
  deductionColumnCount: number
): number {
  return deductionColumnCount + CUADRO_EXTENDED_COL_TOTAL_DEDUCCIONES;
}

export function getGroupedCuadroDeductionsForRow(
  deducciones: CuadroExtendedBonus[],
  totalDeducciones: number
): GroupedDeductionRow[] {
  const grouped = groupDeductionsForRecibo(
    deducciones.map((ded) => ({ name: ded.name, amount: ded.amount }))
  );

  let sumDetailed = grouped.reduce((sum, row) => sum + row.amount, 0);
  sumDetailed = Math.round(sumDetailed * 100) / 100;
  const backendRounded = Math.round(totalDeducciones * 100) / 100;

  if (backendRounded > sumDetailed + 0.01) {
    const diff = Math.round((backendRounded - sumDetailed) * 100) / 100;
    const otrosKey = normalizeDeductionGroupKey("Otros");
    const existing = grouped.find(
      (row) => normalizeDeductionGroupKey(row.displayName) === otrosKey
    );
    if (existing) {
      existing.amount = Math.round((existing.amount + diff) * 100) / 100;
    } else {
      grouped.push({ displayName: "Otros", amount: diff });
      grouped.sort((a, b) =>
        a.displayName.localeCompare(b.displayName, "es", { sensitivity: "base" })
      );
    }
  }

  return grouped;
}

export function mapCuadroExtendedBonusesByColumn(
  bonos: CuadroExtendedBonus[],
  columns: string[]
): string[] {
  return columns.map((_, index) =>
    formatExtendedAmount(bonos[index]?.amount || 0)
  );
}

export function mapCuadroExtendedDeductionsByColumn(
  deducciones: CuadroExtendedBonus[],
  columns: string[],
  totalDeducciones: number
): string[] {
  const grouped = getGroupedCuadroDeductionsForRow(deducciones, totalDeducciones);
  const amountByKey = new Map<string, number>();
  grouped.forEach((row) => {
    amountByKey.set(normalizeDeductionGroupKey(row.displayName), row.amount);
  });
  return columns.map((column) =>
    formatExtendedAmount(amountByKey.get(normalizeDeductionGroupKey(column)) || 0)
  );
}

export function computeCuadroExtendedColumnSchema(
  entries: Array<{ partnerId: number; record: IPayrollReport }>,
  rowDetailsByPartnerId: Record<
    number,
    {
      paymentDetail?: BuildCuadroExtendedInput["paymentDetail"];
      chartDaysWorked?: {
        freeDaysDates?: string[];
        savedIncidents?: unknown[];
      };
    }
  >,
  fullDayHours: number
): CuadroExtendedColumnSchema {
  let maxBonusCount = 0;
  const deductionKeyToLabel = new Map<string, string>();

  entries.forEach(({ partnerId, record }) => {
    const snapshot = rowDetailsByPartnerId[partnerId];
    const breakdown = buildCuadroExtendedRowBreakdown({
      record,
      fullDayHours,
      paymentDetail: snapshot?.paymentDetail ?? null,
      chartFreeDaysDates: snapshot?.chartDaysWorked?.freeDaysDates,
      savedIncidents: snapshot?.chartDaysWorked?.savedIncidents as
        | any[]
        | undefined,
    });

    maxBonusCount = Math.max(maxBonusCount, breakdown.bonos.length);

    getGroupedCuadroDeductionsForRow(
      breakdown.deducciones,
      breakdown.totalDeducciones
    ).forEach((row) => {
      const key = normalizeDeductionGroupKey(row.displayName);
      if (!deductionKeyToLabel.has(key)) {
        deductionKeyToLabel.set(key, row.displayName);
      }
    });

    const summaryDeductionLines = collectReciboDeductionLines({
      deductionBreakdown: (
        record.paymentDetails as { deductionBreakdown?: unknown[] } | undefined
      )?.deductionBreakdown,
      calculatedNewDeductions: record.calculatedNewDeductions,
      calculatedMissingBoxes: record.calculatedMissingBoxes,
    });
    if (parseDeductionAmount(record.tarjetasDeductions) > 0) {
      summaryDeductionLines.push({
        name: "Tarjetas",
        amount: parseDeductionAmount(record.tarjetasDeductions),
      });
    }
    groupDeductionsForRecibo(summaryDeductionLines).forEach((row) => {
      const key = normalizeDeductionGroupKey(row.displayName);
      if (!deductionKeyToLabel.has(key)) {
        deductionKeyToLabel.set(key, row.displayName);
      }
    });
  });

  const bonusColumns = Array.from(
    { length: maxBonusCount },
    (_, index) => `Bono ${index + 1}`
  );
  const deductionColumns = Array.from(deductionKeyToLabel.values()).sort((a, b) =>
    a.localeCompare(b, "es", { sensitivity: "base" })
  );

  return {
    bonusColumns,
    deductionColumns,
    columnCount: getCuadroExtendedColumnCount(
      bonusColumns.length,
      deductionColumns.length
    ),
  };
}

/** @deprecated Usar computeCuadroExtendedColumnSchema().columnCount */
export const CUADRO_EXTENDED_COLUMN_COUNT = getCuadroExtendedColumnCount(0, 0);

export function getCuadroExtendedExcelSubheaderLabels(
  bonusColumns: string[],
  deductionColumns: string[]
): string[] {
  return [
    "Nombre",
    "Puesto",
    "Sueldo base 15",
    "Salario diario",
    "Sueldo x hora",
    "Jorn. compl.",
    "$ Jornadas",
    "H. trab.",
    "$ H. trab.",
    "D. vac.",
    "$ Vac.",
    "Prima vac.",
    "D. incap.",
    "$ Incap.",
    "H. extras",
    "$ H. ext.",
    "T. dobles",
    "$ T. dob.",
    "Desc. trab.",
    "$ D. trab.",
    "D. fest.",
    "$ Fest.",
    ...bonusColumns,
    "Tot. ingresos",
    ...deductionColumns,
    "Tot. desc.",
    "Pago tarj.",
    "Efectivo",
    "Total pagar",
  ];
}

export function getCuadroExtendedExcelPercepcionesGroupColSpan(
  bonusColumnCount: number
): number {
  return (
    2 +
    CUADRO_EXTENDED_COL_SUELDOS +
    CUADRO_EXTENDED_COL_PERCEPCIONES +
    bonusColumnCount +
    CUADRO_EXTENDED_COL_TOTAL_INGRESOS
  );
}

export type CuadroExtendedExportSnapshot = {
  paymentDetail?: BuildCuadroExtendedInput["paymentDetail"];
  chartDaysWorked?: {
    detailed?: any[];
    freeDaysDates?: string[];
    savedIncidents?: unknown[];
  };
};

function titleCaseName(text: string): string {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(/\s+/)
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

export function buildCuadroExtendedExportRowValues(input: {
  record: IPayrollReport;
  snapshot?: CuadroExtendedExportSnapshot | null;
  fullDayHours: number;
  bonusColumns: string[];
  deductionColumns: string[];
}): string[] {
  const { record, snapshot, fullDayHours, bonusColumns, deductionColumns } =
    input;

  const breakdown = buildCuadroExtendedRowBreakdown({
    record,
    fullDayHours,
    paymentDetail: snapshot?.paymentDetail ?? null,
    chartFreeDaysDates: snapshot?.chartDaysWorked?.freeDaysDates,
    savedIncidents: snapshot?.chartDaysWorked?.savedIncidents as
      | any[]
      | undefined,
  });

  const chartDetailed = snapshot?.chartDaysWorked?.detailed || [];
  const chartSummary =
    chartDetailed.length > 0
      ? summarizeChartDays(chartDetailed, fullDayHours)
      : null;

  const jornadasQty =
    chartSummary && chartSummary.completeDays > 0
      ? chartSummary.completeDays
      : breakdown.jornadasCompletas.qty;

  const bonusCells = mapCuadroExtendedBonusesByColumn(
    breakdown.bonos,
    bonusColumns
  );
  const deductionCells = mapCuadroExtendedDeductionsByColumn(
    breakdown.deducciones,
    deductionColumns,
    breakdown.totalDeducciones
  );

  const chilitoName = titleCaseName(record.name || "");
  const chilitoPosition = record.position || "";

  const pair = (metric: CuadroExtendedMetric) => [
    formatExtendedQty(metric.qty),
    formatExtendedAmount(metric.amount),
  ];

  return [
    chilitoName,
    chilitoPosition,
    formatPayrollMoneyLabel(record.salaryPerFortnight),
    formatPayrollMoneyLabel(record.dailySalary),
    formatPayrollMoneyLabel(record.payPerHour),
    formatExtendedQty(jornadasQty),
    formatExtendedAmount(breakdown.jornadasCompletas.amount),
    ...pair(breakdown.horasTrabajadas),
    ...pair(breakdown.vacaciones),
    formatExtendedAmount(breakdown.primaVacacional),
    ...pair(breakdown.incapacidad),
    ...pair(breakdown.horasExtras),
    ...pair(breakdown.turnosDobles),
    ...pair(breakdown.descansoTrabajado),
    ...pair(breakdown.diasFestivos),
    ...bonusCells,
    formatExtendedAmount(breakdown.totalIngresos),
    ...deductionCells,
    formatExtendedAmount(breakdown.totalDeducciones),
    formatExtendedAmount(0),
    formatExtendedAmount(breakdown.totalAPagar),
    formatExtendedAmount(breakdown.totalAPagar),
  ];
}

export function computeEfectivoAPagar(
  totalAPagar: number,
  tarjetas: number
): number {
  return Math.max(0, totalAPagar - tarjetas);
}
